import { UserProfile } from '@shared/types/user';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingService } from '../services/onboardingService';
import { useOnboardingStore } from '../store/onboardingStore';
import {
  OnboardingProgress,
  OnboardingStep,
  OrganizationInfo,
  OrganizationType,
} from '../types';

/**
 * Hook for managing onboarding process
 */
export const useOnboarding = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [progressState, setProgressState] = useState<OnboardingProgress | null>(
    null
  );

  // Get state and actions from the store
  const {
    currentStep,
    organizationType,
    organizationInfo,
    profileInfo,
    completed,
    receiveNotifications,
    isInvitedUser,
    invitationId,
    organizationId,
    setCurrentStep,
    setOrganizationType,
    setOrganizationInfo,
    setProfileInfo,
    setCompleted,
    setReceiveNotifications,
    setInvitedUser,
    resetOnboarding,
  } = useOnboardingStore();

  /**
   * Initialize onboarding by fetching current progress from the backend
   * @returns The onboarding progress data
   */
  const initializeOnboarding = async () => {
    try {
      if (progressState) {
        return progressState;
      }
      setLoading(true);
      setError(null);

      if (completed) {
        // If route has onboarding in it, redirect to /dashboard
        if (window.location.pathname.includes('/onboarding')) {
          navigate('/');
        }
        return;
      }

      const progress = await onboardingService.getProgress();
      setProgressState(progress);

      // Update store with backend data
      if (progress.organizationType) {
        setOrganizationType(progress.organizationType as OrganizationType);
      }

      if (progress.organizationInfo) {
        setOrganizationInfo(progress.organizationInfo);
      }

      if (progress.profileInfo) {
        setProfileInfo(progress.profileInfo);
      }

      if (typeof progress.receiveNotifications === 'boolean') {
        setReceiveNotifications(progress.receiveNotifications);
      }

      // Update invitation status from backend
      if (progress.isInvitedUser && progress.invitationId && progress.organizationId) {
        setInvitedUser(progress.isInvitedUser, progress.invitationId, progress.organizationId);
      }

      setCurrentStep(progress.currentStep as OnboardingStep);
      setCompleted(progress.completed);

      // Redirect to the appropriate step if not completed
      if (!progress.completed) {
        console.log('Redirecting to step:', progress.currentStep);
        redirectToCurrentStep(progress.currentStep as OnboardingStep);
      } else {
        navigate('/');
      }

      // Return the progress data for other hooks to use
      return progress;
    } catch (err) {
      console.error('Error initializing onboarding:', err);
      setError('Failed to load onboarding progress. Please try again.');
      // Return a default progress object in case of error
      return {
        completed: false,
        currentStep: OnboardingStep.ORGANIZATION_TYPE,
      };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Redirect to the appropriate step based on current progress
   */
  const redirectToCurrentStep = (step: OnboardingStep) => {
    switch (step) {
      case OnboardingStep.ORGANIZATION_TYPE:
        navigate('/onboarding-1');
        break;
      case OnboardingStep.ORGANIZATION_INFO:
        navigate('/onboarding-2');
        break;
      case OnboardingStep.PROFILE_INFO:
        navigate('/onboarding-3');
        break;
      case OnboardingStep.COMPLETE:
        navigate('/onboarding-4');
        break;
      default:
        navigate('/onboarding-1');
    }
  };

  /**
   * Save organization type and proceed to next step
   */
  const saveOrganizationType = async (
    type: OrganizationType,
    receiveNotifications: boolean
  ) => {
    try {
      setLoading(true);
      setError(null);

      await onboardingService.updateOrganizationType(
        type,
        receiveNotifications
      );
      setOrganizationType(type);
      setReceiveNotifications(receiveNotifications);

      // Navigate to next step
      navigate('/onboarding-2');
    } catch (err) {
      console.error('Error saving organization type:', err);
      setError('Failed to save organization type. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Save organization information and proceed to next step
   */
  const saveOrganizationInfo = async (info: OrganizationInfo) => {
    try {
      setLoading(true);
      setError(null);

      await onboardingService.updateOrganizationInfo(info);
      setOrganizationInfo(info);

      // Navigate to next step
      navigate('/onboarding-3');
    } catch (err) {
      console.error('Error saving organization info:', err);
      setError('Failed to save organization information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Save profile information and proceed to next step
   */
  const saveProfileInfo = async (info: Partial<UserProfile>) => {
    try {
      setLoading(true);
      setError(null);

      await onboardingService.updateProfileInfo(info);
      setProfileInfo(info);

      // Navigate to next step
      navigate('/onboarding-4');
    } catch (err) {
      console.error('Error saving profile info:', err);
      setError('Failed to save profile information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Complete the onboarding process
   */
  const completeOnboarding = async () => {
    try {
      setLoading(true);
      setError(null);

      await onboardingService.completeOnboarding();
      setCompleted(true);

      // Navigate to dashboard
      navigate('/');
    } catch (err) {
      console.error('Error completing onboarding:', err);
      setError('Failed to complete onboarding. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Search for organizations by name
   */
  const searchOrganizations = async (name: string) => {
    if (!name || name.length < 2) return [];

    try {
      return await onboardingService.searchOrganizations(name);
    } catch (err) {
      console.error('Error searching organizations:', err);
      return [];
    }
  };

  return {
    // State
    currentStep,
    organizationType,
    organizationInfo,
    profileInfo,
    completed,
    receiveNotifications,
    isInvitedUser,
    invitationId,
    organizationId,
    loading,
    error,

    // Actions
    initializeOnboarding,
    saveOrganizationType,
    saveOrganizationInfo,
    saveProfileInfo,
    completeOnboarding,
    searchOrganizations,
    setReceiveNotifications,
    setError: (message: string | null) => setError(message),
    resetOnboarding,
  };
};
